import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  Button,
  Stack,
  Chip,
} from '@mui/material';
import apps from '../data/apps';
import articles from '../data/articles.json';

const graphBg = encodeURIComponent(`
  <svg width="1440" height="640" viewBox="0 0 1440 640" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="glow" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stop-color="rgba(255,255,255,0.18)" />
        <stop offset="50%" stop-color="rgba(255,255,255,0.36)" />
        <stop offset="100%" stop-color="rgba(255,255,255,0.18)" />
      </linearGradient>
      <radialGradient id="halo" cx="50%" cy="40%" r="60%">
        <stop offset="0%" stop-color="rgba(255,255,255,0.12)" />
        <stop offset="100%" stop-color="rgba(255,255,255,0)" />
      </radialGradient>
    </defs>

    <rect width="1440" height="640" fill="url(#halo)" />

    <!-- soft grid -->
    <g stroke="rgba(255,255,255,0.08)" stroke-width="1">
      ${Array.from({ length: 11 }, (_, i) => {
        const y = 70 + i * 48;
        return `<line x1="0" y1="${y}" x2="1440" y2="${y}" />`;
      }).join('')}
      ${Array.from({ length: 14 }, (_, i) => {
        const x = i * 110;
        return `<line x1="${x}" y1="0" x2="${x}" y2="640" />`;
      }).join('')}
    </g>

    <!-- main growth curve -->
    <path d="M0,420 C180,360 300,460 480,390 C660,320 780,460 960,390 C1140,320 1300,360 1440,330"
      fill="none" stroke="url(#glow)" stroke-width="3.5"/>

    <!-- secondary dashed trend -->
    <path d="M0,470 C220,430 360,470 520,430 C700,380 880,440 1060,400 C1240,360 1340,380 1440,360"
      fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="2" stroke-dasharray="12 8"/>

    <!-- ascending growth lines -->
    <path d="M0,540 C240,480 420,440 600,360 C800,270 1040,250 1280,210"
      fill="none" stroke="rgba(255,255,255,0.24)" stroke-width="2.5"/>
    <path d="M0,510 C220,450 440,400 680,330 C940,250 1160,230 1440,190"
      fill="none" stroke="rgba(255,255,255,0.18)" stroke-width="2" stroke-dasharray="10 6"/>
    <path d="M0,560 C260,520 520,470 760,400 C1000,330 1200,300 1400,260"
      fill="none" stroke="rgba(255,255,255,0.16)" stroke-width="1.8" stroke-dasharray="6 8"/>

    <!-- tertiary dotted baseline -->
    <path d="M0,520 C240,520 480,500 720,520 C960,540 1200,530 1440,510"
      fill="none" stroke="rgba(255,255,255,0.14)" stroke-width="1.5" stroke-dasharray="4 6"/>

  </svg>
`);

const kitFormOptions = JSON.stringify({
  settings: {
    after_subscribe: {
      action: 'message',
      success_message: 'Success! Now check your email to confirm your subscription.',
      redirect_url: '',
    },
    analytics: {
      google: null,
      fathom: null,
      facebook: null,
      segment: null,
      pinterest: null,
      sparkloop: null,
      googletagmanager: null,
    },
    modal: {
      trigger: 'timer',
      scroll_percentage: null,
      timer: 5,
      devices: 'all',
      show_once_every: 15,
    },
    powered_by: {
      show: true,
      url: 'https://kit.com/features/forms?utm_campaign=poweredby&utm_content=form&utm_medium=referral&utm_source=dynamic',
    },
    recaptcha: {
      enabled: false,
    },
    return_visitor: {
      action: 'show',
      custom_content: '',
    },
    slide_in: {
      display_in: 'bottom_right',
      trigger: 'timer',
      scroll_percentage: null,
      timer: 5,
      devices: 'all',
      show_once_every: 15,
    },
    sticky_bar: {
      display_in: 'top',
      trigger: 'timer',
      scroll_percentage: null,
      timer: 5,
      devices: 'all',
      show_once_every: 15,
    },
  },
  version: '5',
});

const KitNewsletterSignup = () => {
  useEffect(() => {
    if (document.getElementById('kit-embed-script')) return;

    const script = document.createElement('script');
    script.id = 'kit-embed-script';
    script.src = 'https://f.convertkit.com/ckjs/ck.5.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 5, md: 6 },
        background: '#eef4fb',
        borderTop: '1px solid rgba(15, 23, 42, 0.08)',
        borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
      }}
    >
      <Container>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '0.85fr 1.15fr' },
            gap: 3,
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h2"
              sx={{ mb: 1, fontWeight: 700 }}
            >
              Building calmer, clearer ways to understand money
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', maxWidth: 520, mb: 1 }}
            >
              Get updates when new calculators, AI tools and practical money resources are released.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', maxWidth: 520 }}
            >
              No spam. Just useful stuff.
            </Typography>
          </Box>

          <Box
            sx={{
              '& .formkit-form': {
                maxWidth: 'none',
                width: '100%',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
              },
              '& .formkit-form *': {
                boxSizing: 'border-box',
              },
              '& [data-style="clean"]': {
                width: '100%',
              },
              '& .formkit-alert': {
                width: '100%',
                listStyle: 'none',
                m: '0 0 1rem',
                p: 1.5,
                borderRadius: 1,
                bgcolor: '#fff',
                border: '1px solid #e3e3e3',
                textAlign: 'center',
              },
              '& .formkit-alert:empty': {
                display: 'none',
              },
              '& .formkit-fields': {
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' },
                gap: 1.25,
                alignItems: 'stretch',
              },
              '& .formkit-field': {
                minWidth: 0,
              },
              '& .formkit-input': {
                width: '100%',
                height: 48,
                px: 1.75,
                py: 1.25,
                border: '1px solid #e3e3e3 !important',
                borderRadius: '4px !important',
                color: '#000000 !important',
                bgcolor: '#fff',
                font: 'inherit',
                fontWeight: '400 !important',
                outline: 'none',
                transition: 'border-color 0.18s ease',
              },
              '& .formkit-input:focus': {
                borderColor: '#1976d2 !important',
              },
              '& .formkit-submit': {
                minHeight: 48,
                px: { xs: 0, sm: 0 },
                border: 0,
                borderRadius: '3px !important',
                bgcolor: '#1976d2 !important',
                color: '#fff !important',
                cursor: 'pointer',
                font: 'inherit',
                fontWeight: '400 !important',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              },
              '& .formkit-submit > span': {
                display: 'block',
                px: 2.5,
                py: 1.35,
              },
              '& .formkit-submit:hover, & .formkit-submit:focus': {
                bgcolor: '#1565c0 !important',
                outline: 'none',
              },
              '& .formkit-spinner': {
                display: 'none',
              },
              '& .formkit-powered-by-convertkit-container': {
                mt: 1.25,
                display: 'flex',
                justifyContent: { xs: 'center', md: 'flex-end' },
              },
              '& .formkit-powered-by-convertkit': {
                color: 'text.secondary',
                fontSize: '0.76rem',
                textDecoration: 'none',
              },
            }}
          >
            <form
              action="https://app.kit.com/forms/9483016/subscriptions"
              className="seva-form formkit-form"
              method="post"
              data-sv-form="9483016"
              data-uid="993482d20d"
              data-format="inline"
              data-version="5"
              data-options={kitFormOptions}
              min-width="400 500 600 700 800"
            >
              <div data-style="clean">
                <ul className="formkit-alert formkit-alert-error" data-element="errors" data-group="alert" />
                <div data-element="fields" data-stacked="false" className="seva-fields formkit-fields">
                  <div className="formkit-field">
                    <input
                      className="formkit-input"
                      aria-label="First Name"
                      name="fields[first_name]"
                      placeholder="First Name"
                      type="text"
                    />
                  </div>
                  <div className="formkit-field">
                    <input
                      className="formkit-input"
                      name="email_address"
                      aria-label="Email Address"
                      placeholder="Email Address"
                      required
                      type="email"
                    />
                  </div>
                  <button data-element="submit" className="formkit-submit" type="submit">
                    <div className="formkit-spinner">
                      <div />
                      <div />
                      <div />
                    </div>
                    <span>Subscribe</span>
                  </button>
                </div>
                <div className="formkit-powered-by-convertkit-container">
                  <a
                    href="https://kit.com/features/forms?utm_campaign=poweredby&utm_content=form&utm_medium=referral&utm_source=dynamic"
                    data-element="powered-by"
                    className="formkit-powered-by-convertkit"
                    data-variant="dark"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                  >
                    Built with Kit
                  </a>
                </div>
              </div>
            </form>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const Home = () => {
  const featuredApps = apps.filter(app => app.featured).slice(0, 4); // Top featured apps
  const featuredArticles = articles.filter((article) => article.featured);
  const latestArticles = [...featuredArticles]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3); // Top 3 recent

  return (
    <>
      <Helmet>
        <title>Personal Finance Tools & Guides | Calculators for Savings, Mortgages, Budgeting</title>
        <meta
          name="description"
          content="Explore calculators, tools, and articles to help you plan smarter, save better, and make confident money moves."
        />
        <link rel="canonical" href="https://talkabout.finance/" />
      </Helmet>

      {/* HERO */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#0a1f45',
          color: 'common.white',
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #0a1f45 0%, #0b2d59 45%, #0d3c73 100%)',
        }}
      >
        {/* subtle gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 30% 40%, rgba(255,255,255,0.12), rgba(255,255,255,0)), linear-gradient(135deg, rgba(255,255,255,0.04), rgba(0,0,0,0.18))',
          }}
        />
        {/* graph line overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url("data:image/svg+xml,${graphBg}")`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            opacity: 0.5,
          }}
        />
        <Container sx={{ position: 'relative' }}>
          <Chip
            label="Personal Finance Hub"
            size="small"
            sx={{
              mb: 2,
              bgcolor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.3)',
            }}
            variant="outlined"
          />
          <Typography
            variant="h2"
            component="h1"
            sx={{
              mb: 2,
              fontWeight: 800,
              lineHeight: 1.1,
              fontSize: { xs: '1.75rem', sm: '2.6rem', md: '3.3rem' },
            }}
          >
            <Box component="span" sx={{ display: 'block', whiteSpace: { xs: 'nowrap', sm: 'normal' } }}>
              We explain the numbers.
            </Box>
            <Box component="span" sx={{ display: 'block', whiteSpace: { xs: 'nowrap', sm: 'normal' } }}>
              You make the choice.
            </Box>
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Clear, practical financial tools and explanations to help you make decisions that are right for you.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              component={Link}
              to="/apps"
              variant="contained"
              color="secondary"
              size="large"
            >
              View Apps
            </Button>
            <Button
              component={Link}
              to="/articles"
              variant="outlined"
              color="inherit"
              size="large"
              sx={{ borderColor: 'rgba(255,255,255,0.7)' }}
            >
              Read Articles
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* About Section */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 7, md: 9 },
          background: 'radial-gradient(circle at 15% 20%, rgba(71, 130, 218, 0.18), rgba(255,255,255,0) 60%), linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
          borderTop: '1px solid rgba(15, 23, 42, 0.08)',
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'radial-gradient(circle at 85% 35%, rgba(239, 111, 108, 0.22), rgba(255,255,255,0) 55%)',
            pointerEvents: 'none',
          }}
        />
        <Container sx={{ position: 'relative' }}>
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: '1fr',
            }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: '#1b2a41',
                  letterSpacing: '0.08em',
                  fontWeight: 700,
                  mb: 1.5,
                }}
              >
                About Talkabout.finance
              </Typography>
              <Typography
                variant="h3"
                component="h2"
                sx={{ mb: 2, fontWeight: 800, color: '#0f1d33' }}
              >
                Knowledge that helps you decide with confidence
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  width: '100%',
                  maxWidth: 'none',
                  color: '#384558',
                  fontSize: '1.08rem',
                  lineHeight: 1.7,
                  textAlign: 'justify',
                }}
              >
                Money decisions can feel overwhelming. We break things down clearly, using real numbers and simple
                explanations, so you can understand your options and choose what works best for your life.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Featured Apps Section */}
      <Box
        component="section"
        sx={{
          py: { xs: 5, md: 6 },
          background: '#f8fafc',
        }}
      >
        <Container>
          <Box>
          <Typography variant="h5" gutterBottom>Featured Apps</Typography>
          <Typography
            variant="body1"
            sx={{ maxWidth: 720, color: 'text.secondary', mb: 3 }}
          >
            Simple tools to explore the numbers at your own pace.
          </Typography>
          <Grid container spacing={3}>
            {featuredApps.map(app => (
              <Grid item xs={12} sm={6} md={4} key={app.slug}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Link to={app.link} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={app.hero}
                        alt={app.title}
                        sx={{
                          filter: 'brightness(0.96) contrast(0.97) saturate(0.94)',
                        }}
                      />
                      <Box
                        aria-hidden="true"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background:
                            'linear-gradient(0deg, rgba(31, 41, 55, 0.34) 0%, rgba(107, 114, 128, 0.14) 42%, rgba(255, 255, 255, 0) 100%)',
                        }}
                      />
                      {app.attribution && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            bgcolor: 'rgba(0, 0, 0, 0.45)',
                            color: '#fff',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            backdropFilter: 'blur(4px)',
                          }}
                        >
                          <a
                            href={app.attribution.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#fff', textDecoration: 'none', opacity: 0.9 }}
                          >
                            Photo: {app.attribution.name}
                          </a>
                        </Box>
                      )}
                    </Box>

                    <CardContent>
                      <Typography variant="h6" component="div">
                        {app.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {app.description}
                      </Typography>
                    </CardContent>
                  </Link>
                </Card>
              </Grid>
            ))}
          </Grid>
          </Box>
        </Container>
      </Box>

      <KitNewsletterSignup />

      {/* Latest Articles Section */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 6, md: 8 },
          background: '#f8fafc',
          borderTop: '1px solid rgba(15, 23, 42, 0.08)',
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
        }}
      >
        <Container>
          <Typography variant="h5" gutterBottom>Latest Articles</Typography>
          <Typography
            variant="body1"
            sx={{ maxWidth: 720, color: 'text.secondary', mb: 3 }}
          >
            Discussions that explore the thinking behind money choices, not instructions.
          </Typography>
          <Grid container spacing={3}>
            {latestArticles.map(article => (
              <Grid item xs={12} sm={6} md={4} key={article.slug}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Link to={`/articles/${article.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    {article.hero && (
                      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                        <CardMedia
                          component="img"
                          height="140"
                          image={article.hero}
                          alt={article.title}
                          sx={{
                            filter: 'brightness(0.96) contrast(0.97) saturate(0.94)',
                          }}
                        />
                        <Box
                          aria-hidden="true"
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            background:
                              'linear-gradient(0deg, rgba(31, 41, 55, 0.34) 0%, rgba(107, 114, 128, 0.14) 42%, rgba(255, 255, 255, 0) 100%)',
                          }}
                        />
                      </Box>
                    )}
                    <CardContent>
                      <Typography variant="h6" component="div">
                        {article.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {article.summary}
                      </Typography>
                    </CardContent>
                  </Link>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Home;
